<template>
  <div class="m-b-lg">
    <template v-if="selectedProperty.opa_account_num">
      <div class="p-md has-background-light-blue border-bottom is-light-ben-franklin">
        <div class="container content">
          <p class="has-text-weight-bold m-none">
            {{ $wording.ec.text_1 }}
            <help-icon modal="opa-info" />
          </p>
          <p v-html="$wording.home.property_assessment_text_2" />
        </div>
      </div>
      <div class="p-md has-background-light-blue">
        <div class="container">
          <div class="level">
            <div class="level-item">
              <div>
                <p class="heading">
                  {{ $wording.ec.text_2 }}
                </p>
                <p class="title">
                  {{ selectedProperty.opa_account_num }}
                </p>
              </div>
            </div>
            <div class="level-item">
              <div>
                <p class="heading">
                  {{ $wording.ec.text_3 }}
                </p>
                <p class="title">
                  {{ propertyAddress }}
                </p>
              </div>
            </div>
            <div class="level-item">
              <div>
                <p class="heading">
                  {{ $wording.ec.text_4 }}
                </p>
                <p
                  class="title"
                  data-hj-suppress
                >
                  {{ propertyOwners }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <div class="container m-t-lg">
      <div class="columns">
        <div class="column">
          <validation-observer
            id="property-form"
            ref="property-form"
            tag="form"
            class="normal-color-for-disabled-inputs"
            @submit.prevent
          >
            <div class="content">
              <h2>{{ $wording.ec.title }}</h2>
              <p v-html="$wording.ec.introduction" />
            </div>

            <div class="field">
              <p
                class="label radio-desc required"
              >
                {{ $wording.ec.text_5 }}
                <help-icon modal="helper-before-1978" />
              </p>
              <div class="control">
                <radio-group-with-validation
                  v-model="prior1978"
                  :options="{ 'yes': 'Yes', 'no': 'No' }"
                  class="is-grouped radio-wrap required"
                  title="Before 1978"
                  name="before1978"
                  data-hj-suppress
                  required
                />
              </div>
            </div>

            <div
              v-if="prior1978 === 'no' && mightBeBefore1978"
              class="notification content"
            >
              <p
                v-if="!isAdmin"
                v-html="$sprintf($wording.ec.text_6, propertyAddress)"
              />
              <p
                v-else
                v-text="$wording.ec.build_prior_1978"
              />
              <div
                v-if="!isAdmin"
                class="field is-grouped"
              >
                <div class="control">
                  <checkbox-with-validation
                    v-model="yearAssert"
                    :value="true"
                    placeholder="confirmation"
                    name="yearAssert"
                    required
                  >
                    {{ $wording.ec.text_7 }}
                  </checkbox-with-validation>
                </div>
              </div>
              <div
                v-if="!isAdmin"
                class="field"
              >
                <p class="label m-b-none">
                  Supporting documentation <help-icon modal="accepted-files" />
                </p>
                <p>You might submit documentation to confirm that your property was built after 1978.</p>
                <div class="control">
                  <div
                    v-for="(file, index) in after1978Support"
                    :key="index"
                    class="field"
                  >
                    <div class="control">
                      <validation-provider
                        v-slot="{ errors, reset }"
                        :rules="{ ext: [ 'pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx' ] }"
                        name="Support file"
                        :vid="`support-file`"
                        mode="passive"
                      >
                        <file
                          ref="attach-support"
                          v-model="after1978Support[index]"
                          placeholder="Attach support"
                          name="supportFile"
                          title="Choose file"
                        >
                          <p><small>The allowed file extensions are pdf, jpg, jpeg, png, doc and docx.</small></p>
                        </file>
                        <transition name="fade">
                          <span
                            v-if="errors[0]"
                            class="cell shrink error popover"
                            tabindex="0"
                            @click="reset()"
                            v-text="errors[0]"
                          />
                        </transition>
                      </validation-provider>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="!isAdmin"
                class="field is-grouped is-grouped-right"
              >
                <div
                  v-if="after1978Support.length > 1"
                  class="control"
                >
                  <button
                    type="button"
                    class="button is-outlined is-small is-danger"
                    @click.prevent="after1978Support.splice(-1,1)"
                  >
                    <span class="icon"><i class="fas fa-trash" /></span>
                    <span>Remove last file</span>
                  </button>
                </div>
                <div class="control">
                  <button
                    type="button"
                    class="button is-primary is-outlined is-small"
                    @click.prevent="after1978Support.push(null)"
                  >
                    <span class="icon"><i class="fas fa-plus" /></span>
                    <span>Add file</span>
                  </button>
                </div>
              </div>
            </div>
            <div class="" />

            <div class="field">
              <p class="label radio-desc required">
                {{ $wording.ec.text_8 }}
                <help-icon modal="residential-daycare" />
              </p>
              <div class="control">
                <radio-group-with-validation
                  v-model="propertyType"
                  :options="{ 'Residential': 'Residential', 'Daycare': 'Family daycare' }"
                  class="is-grouped radio-wrap required"
                  title="Property Type"
                  name="propertyType"
                  required
                />
              </div>
            </div>

            <template v-if="propertyType === 'Residential'">
              <div class="field">
                <p class="label radio-desc required">
                  {{ $wording.ec.text_9 }}
                  <help-icon modal="child-under-6" />
                </p>
                <div class="control">
                  <radio-group-with-validation
                    v-model="childUnderSix"
                    :options="{ 'yes': 'Yes', 'no': 'No' }"
                    class="is-grouped radio-wrap required"
                    :title="$wording.ec.text_10"
                    name="childUnderSix"
                    required
                  />
                </div>
              </div>
            </template>

            <template v-if="propertyType!==''">
              <div class="field">
                <div class="control">
                  <p class="label">
                    {{ $wording.ec.rental_license_number }}
                    <help-icon modal="rental-license" />
                  </p>
                  <input-text-with-validation
                    v-if="!dontHaveLicenseNumber"
                    v-model="licenseNumber"
                    name="licenseNumber"
                    maxlength="7"
                    placeholder="License number"
                    class="input-width-auto"
                    :rules="{ required: !isAdmin }"
                    :required="!isAdmin ? 'required' : undefined"
                    @input.native.stop="maxNumericLength"
                  />
                </div>
                <div
                  v-if="Object.keys(foundLicenseData).length > 0 && !errorOPADoesntMatch"
                  class="notification license-found is-info is-light m-b-sm"
                >
                  <p v-show="serverNumberOfUnits">
                    {{ $wording.ec.number_of_units }}: <strong>{{ serverNumberOfUnits }}</strong>
                  </p>
                  <p data-hj-suppress>
                    {{ $wording.ec.legal_name }}: <strong>{{ foundLicenseData.legalname }}</strong>
                  </p>
                  <p>{{ $wording.ec.status }}: <strong>{{ foundLicenseData.licensestatus }}</strong></p>
                  <small v-html="$wording.ec.license_help_text" />
                </div>
                <p
                  v-show="errorLicenseNotFound"
                  class="notification error-license-no-found is-warning m-b-sm"
                >
                  <span v-html="$sprintf($wording.ec.license_no_found, licenseNumber)" />
                  <br>
                  <small v-html="$wording.ec.license_help_text" />
                </p>
                <p
                  v-show="errorOPADoesntMatch"
                  class="notification error-opa-doesnt-match is-warning m-b-sm"
                >
                  <span v-html="$sprintf($wording.ec.license_opa_doesnt_match, licenseNumber, foundLicenseData.address)" />
                  <br>
                  <small v-html="$wording.ec.license_help_text" />
                </p>
              </div>
              <div
                v-if="!isAdmin"
                class="field"
              >
                <div class="control m-b-md">
                  <checkbox
                    v-model="dontHaveLicenseNumber"
                    :disabled="lookingForLicense"
                    :value="true"
                    :placeholder="$wording.ec.text_13"
                    name="dontHaveLicenseNumber"
                  >
                    {{ $wording.ec.text_13 }}
                  </checkbox>
                </div>
              </div>
            </template>

            <div
              v-if="propertyType === 'Residential' && !foundLicenseData.numberofunits && (dontHaveLicenseNumber || isAdmin)"
              class="field is-narrow"
            >
              <div class="control">
                <p class="label">
                  {{ $wording.ec.text_11 }}
                  <help-icon modal="number-of-units-helper" />
                </p>
                <input-text-with-validation
                  v-model.number="numberOfUnits"
                  name="numberOFUnits"
                  rules="required|numeric"
                  type="text"
                  maxlength="5"
                  placeholder="Number of units"
                  class="input-width-auto"
                  required
                  @input.native.stop="maxNumericLength"
                />
              </div>
            </div>

            <template v-if="dontHaveLicenseNumber || isAdmin">
              <hr>
              <div class="content">
                <h3
                  v-text="$wording.ec.title_2"
                />
                <p v-text="$wording.ec.text_14" />
              </div>
              <div class="field">
                <div class="control">
                  <p
                    class="label m-b-md is-block"
                    v-text="$wording.ec.text_15"
                  />
                  <radio-group-with-validation
                    v-model="proprietorType"
                    :options="{ 'Individual': 'Individual', 'Company': 'Company' }"
                    class="is-grouped radio-wrap required"
                    :title="$wording.ec.text_16"
                    name="proprietorType"
                    required
                  />
                </div>
              </div>

              <div
                v-if="proprietorType==='Individual'"
                class="field is-grouped m-t-md"
              >
                <div class="control is-expanded">
                  <input-text-with-validation
                    v-model="proprietorFirstName"
                    name="proprietorFirstName"
                    rules="required"
                    :placeholder="$wording.register.text_1"
                    required
                  />
                </div>
                <div class="control is-expanded">
                  <textbox
                    v-model="proprietorMiddleName"
                    :placeholder="$wording.register.text_13"
                  />
                </div>
                <div class="control is-expanded">
                  <input-text-with-validation
                    v-model="proprietorLastName"
                    name="propriettorLastName"
                    rules="required"
                    :placeholder="$wording.register.text_2"
                    required
                  />
                </div>
              </div>

              <div
                v-if="proprietorType==='Company'"
                class="field is-grouped m-t-md"
              >
                <div class="control is-expanded">
                  <input-text-with-validation
                    v-model="proprietorCompanyName"
                    name="proprietorCompanyName"
                    rules="required"
                    :placeholder="$wording.register.text_5"
                    required
                  />
                </div>
                <div class="control is-expanded">
                  <input-text-with-validation
                    v-model="proprietorJobTitle"
                    name="proprietorJobTitle"
                    :rules="{ required: !isAdmin }"
                    :placeholder="$wording.ec.text_17"
                    :required="isAdmin ? undefined : true"
                  />
                </div>
              </div>


              <div class="field is-grouped">
                <div class="control is-expanded">
                  <input-text-with-validation
                    v-model="proprietorEmailAddress"
                    name="proprietorEmailAddress"
                    rules="required|email"
                    type="email"
                    placeholder="Email"
                    required
                  />
                </div>
                <div class="control is-expanded">
                  <input-text-with-validation
                    v-model="proprietorPhoneNumber"
                    name="proprietorPhoneNumber"
                    :rules="{ required: !isAdmin }"
                    :placeholder="$wording.register.text_3"
                    :required="isAdmin ? undefined : true"
                  />
                </div>
              </div>

              <div class="content">
                <h4
                  class="m-b-none m-t-md"
                  v-text="$wording.register.title_2"
                />
                <p v-text="$wording.ec.text_18" />
              </div>
              <div class="field is-grouped">
                <div class="control is-expanded">
                  <input-text-with-validation
                    v-model="proprietorStreetAddress1"
                    name="proprietorStreetAddress1"
                    rules="required"
                    :placeholder="$wording.register.text_8"
                    required
                  />
                </div>
                <div class="control is-expanded">
                  <input-text-with-validation
                    v-model="proprietorStreetAddress2"
                    name="proprietorStreetAddress2"
                    :placeholder="$wording.register.text_9"
                  />
                </div>
              </div>
              <div class="field is-grouped field-grouped-half-fix">
                <div class="control is-expanded">
                  <input-text-with-validation
                    v-model="proprietorCity"
                    name="proprietorCity"
                    rules="required"
                    placeholder="City"
                    required
                  />
                </div>
                <div class="control is-expanded">
                  <dropdown-with-validation
                    v-if="proprietorCountry === 'USA'"
                    v-model="proprietorState"
                    name="proprietorState"
                    :default-option="$wording.register.text_10"
                    :options="$store.state.states"
                    rules="required"
                    :placeholder="$wording.register.text_10"
                    required
                  />
                  <input-text-with-validation
                    v-else
                    v-model="proprietorState"
                    name="proprietorState"
                    rules="required"
                    :placeholder="$wording.register.text_10"
                    required
                  />
                </div>
              </div>

              <div class="field is-grouped field-grouped-half-fix">
                <div class="control is-expanded">
                  <dropdown-with-validation
                    v-model="proprietorCountry"
                    :default-option="`Country`"
                    :options="$store.state.countries"
                    text-key="name"
                    value-key="code"
                    rules="required"
                    name="country"
                    placeholder="Country"
                    required
                  />
                </div>
                <div class="control is-expanded">
                  <input-text-with-validation
                    v-model="proprietorZip"
                    name="proprietorZip"
                    rules="required"
                    :placeholder="$wording.register.text_11"
                    required
                  />
                </div>
              </div>
            </template>

            <hr>
            <div
              v-if="!isAdmin"
              class="field"
            >
              <div class="control">
                <checkbox-with-validation
                  v-model="formAssert"
                  name="formAssert"
                  :value="true"
                  placeholder="Accertion"
                  required
                >
                  <span v-html="$wording.ec.text_19" />
                </checkbox-with-validation>
              </div>
            </div>
            <div class="control">
              <div class="field">
                <div class="control has-text-centered">
                  <button
                    class="button is-primary submit-button"
                    :disabled="lookingForLicense"
                    :class="{ 'is-loading': lookingForLicense }"
                    @click.prevent="validateForm"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </validation-observer>
        </div>
      </div>
    </div>

    <modal
      :adaptive="true"
      :scrollable="true"
      name="confirm-data-modal"
      height="auto"
      width="80%"
      @opened="isModalOn"
      @closed="isModalOff"
    >
      <div class="modal custom-modal is-active">
        <div class="modal-card">
          <header class="modal-card-head is-marginless">
            <p
              class="modal-card-title is-marginless"
              v-text="$wording.ec.text_20"
            />
            <button
              class="delete"
              aria-label="close"
              @click="submitting || $modal.hide('confirm-data-modal');"
            />
          </header>
          <section class="modal-card-body">
            <div class="section p-t-md p-b-md">
              <div class="content">
                <p v-html="$wording.ec.text_21" />
                <p
                  class="has-text-weight-semibold"
                  v-text="$wording.ec.text_5"
                />
                <p
                  v-if="yearAssert && prior1978 === 'no'"
                >
                  Requires staff review ({{ after1978Support.filter(file => file !== null).length }} files)
                </p>
                <p v-else>
                  {{ String(prior1978).toProperCase() }}
                </p>

                <p
                  class="has-text-weight-semibold"
                  v-text="$wording.ec.text_23"
                />
                <p>
                  <span v-show="propertyType === 'Residential'">Residential</span>
                  <span v-show="propertyType === 'Daycare'">Family daycare</span>
                </p>

                <template v-if="propertyType === 'Residential'">
                  <p
                    class="has-text-weight-semibold"
                    v-text="$wording.ec.text_9"
                  />
                  <p
                    data-hj-suppress
                    v-text="String(childUnderSix).toProperCase()"
                  />
                  <p
                    class="has-text-weight-semibold"
                    v-text="$wording.ec.text_22"
                  />
                  <p
                    v-if="serverNumberOfUnits && licenseNumber"
                    v-text="$sprintf($wording.ec.text_server_nro_units, numberOfUnits)"
                  />
                  <p
                    v-else
                    v-text="$sprintf($wording.ec.text_user_provided, numberOfUnits)"
                  />
                </template>

                <p
                  class="has-text-weight-semibold"
                  v-text="$wording.ec.text_24"
                />
                <p
                  v-if="licenseNumber"
                  v-text="licenseNumber"
                />
                <p v-else>
                  Not provided
                </p>

                <template v-if="dontHaveLicenseNumber || isAdmin">
                  <p
                    class="has-text-weight-semibold"
                    v-text="$wording.ec.text_25"
                  />
                  <p
                    class="is-capitalized"
                    v-text="proprietorType"
                  />

                  <template v-if="proprietorType === 'Individual'">
                    <p
                      class="has-text-weight-semibold"
                      v-text="$wording.ec.text_26"
                    />
                    <p
                      v-text="proprietorFullName"
                    />
                  </template>

                  <template v-if="proprietorType === 'Company'">
                    <p
                      class="has-text-weight-semibold"
                      v-text="$wording.ec.text_27"
                    />
                    <p

                      v-text="companyAndJobTitle"
                    />
                  </template>

                  <p
                    class="has-text-weight-semibold"
                    v-text="$wording.ec.text_28"
                  />
                  <p
                    v-text="proprietorEmailAddress"
                  />

                  <p
                    class="has-text-weight-semibold"
                    v-text="$wording.register.text_3"
                  />
                  <p
                    v-text="proprietorPhoneNumber"
                  />

                  <p
                    class="has-text-weight-semibold"
                    v-text="$wording.register.title_2"
                  />
                  <p
                    v-text="proprietorFullAddress"
                  />
                </template>

                <template v-if="!isAdmin">
                  <p
                    v-html="$sprintf($wording.ec.you_declared, String(propertyAddress).toUpperCase(), selectedProperty.opa_account_num)"
                  />
                </template>
              </div>
            </div>
          </section>
          <footer class="modal-card-foot is-justified-end">
            <button
              class="button is-dark"
              :disabled="submitting"
              @click="submitting || $modal.hide('confirm-data-modal');"
              v-html="$wording.ec.text_30"
            />
            <button
              class="button confirm-button"
              :class="{ 'is-loading': submitting }"
              @click.prevent="submitForm"
            >
              Confirm
            </button>
          </footer>
        </div>
      </div>
    </modal>

    <!-- more information -->
    <more-info
      id="opa-info"
      title="Confirm property information"
      :text="$wording.ec.helper_opa"
    />
    <more-info
      id="helper-before-1978"
      title="Property built prior to 1978?"
      :text="$wording.ec.helper_before_1978"
    />
    <more-info
      id="residential-daycare"
      title="Residential rental or family daycare"
      :text="$wording.ec.residential_daycare"
    />
    <more-info
      id="child-under-6"
      title="Children age 6 or under"
      :text="$wording.ec.child_under_6"
    />
    <more-info
      id="rental-license"
      title="Business license"
      :text="$wording.ec.rental_license"
    />
    <more-info
      id="number-of-units-helper"
      :title="$wording.ec.number_of_units"
      :text="$wording.ec.number_of_units_helper"
    />

    <more-info
      id="accepted-files"
      title="Acceptable documentation"
    >
      <div
        class="content"
        v-html="$wording.cases.proving_documentation_list"
      />
    </more-info>
  </div>
</template>

<script>
import DropdownWithValidation from '@/components/common/DropdownWithValidation';
import InputTextWithValidation from "@/components/common/InputTextWithValidation";
import CheckboxWithValidation from "@/components/common/CheckboxWithValidation";
import RadioGroupWithValidation from "@/components/common/RadioGroupWithValidation";
import Checkbox from "@/components/common/Checkbox";
import MoreInfo from '@/components/modals/MoreInfo';
import HelpIcon from '@/components/common/HelpIcon';
import File from '@/components/InspectionForm/File';

import { debounce } from 'lodash';
import { objectToFormData } from 'object-to-formdata';

export default {
  name: 'EligibilityCheckForm',
  components: {
    InputTextWithValidation,
    CheckboxWithValidation,
    RadioGroupWithValidation,
    DropdownWithValidation,
    Checkbox,
    MoreInfo,
    HelpIcon,
    File,
  },
  data() {
    return {
      submitting: false,
      mightBeBefore1978: false,

      prior1978: null,
      after1978Support: [ null ],
      yearAssert: null,
      propertyType: '',
      childUnderSix: null,
      numberOfUnits: null,
      licenseNumber: null,

      lookingForLicense: false,
      oneMoreTime: null,
      errorLicenseNotFound: null,
      errorOPADoesntMatch: null,
      serverNumberOfUnits: null,
      foundLicenseData: {},

      proprietorType: null,
      proprietorFirstName: '',
      proprietorMiddleName: '',
      proprietorLastName: '',
      proprietorCompanyName: '',
      proprietorJobTitle: '',
      proprietorEmailAddress: '',
      proprietorPhoneNumber: '',
      proprietorStreetAddress1: '',
      proprietorStreetAddress2: '',
      proprietorCity: '',
      proprietorState: 'PA',
      proprietorCountry: 'USA',
      proprietorZip: '',
      formAssert: false,

      dontHaveLicenseNumber: false,
    };
  },
  computed: {
    selectedProperty() {
      return this.$store.state.properties.selectedProperty || null;
    },
    propertyAddress() {
      return this.$store.getters['properties/selectedPropertyAddress'];
    },
    propertyOwners() {
      return Array(this.selectedProperty.opa_owners).join(',');
    },
    proprietorFullName() {
      return String(`${this.proprietorFirstName}
        ${this.proprietorMiddleName || ''}
        ${this.proprietorLastName}`)
        .replace(/\s+(?= )/g,'');
    },
    companyAndJobTitle() {
      return String(`${this.proprietorCompanyName}, ${this.proprietorJobTitle}`).replace(/\s+(?= )/g,'');
    },
    proprietorFullAddress() {
      return String(`${this.proprietorStreetAddress1}
         ${this.proprietorStreetAddress2 || ''},
         ${this.proprietorCity},
         ${this.proprietorState} ${this.proprietorZip}`).replace(/\s+(?= )/g,'');
    },
    selectedCountry() {
      return this.proprietorCountry;
    },
    typeOfLicenseNumber() {
      if (this.propertyType === 'Residential') {
        return 'Residential';
      }
      if (this.propertyType === 'Daycare') {
        return 'Family daycare';
      }
      return '';
    },
  },
  watch: {
    selectedCountry() {
      this.proprietorState = '';
    },
    dontHaveLicenseNumber(newValue) {
      if (newValue === true) {
        if (this.serverNumberOfUnits !== null) {
          this.numberOfUnits = null;
        }
        this.resetValuesForLicenseLookup();
        this.licenseNumber = null;
      }
    },
    propertyType() {
      if (this.licenseNumber !== null) {
        this.checkLicense();
      }
    },
  },
  async beforeMount() {
    this.mightBeBefore1978 = await this.$store.dispatch(
      'properties/prior1978',
      this.$sCache.get('selectedOPAAccountNumber')
    );
  },
  methods: {
    async resetValuesForLicenseLookup() {
      this.errorLicenseNotFound = null;
      this.errorOPADoesntMatch = null;
      this.serverNumberOfUnits = null;
      this.foundLicenseData = {};
      this.lookingForLicense = false;

      return await this.$nextTick();

    },

    checkLicense: debounce(async function () {
      if (!this.licenseNumber) {
        await this.resetValuesForLicenseLookup();
        return;
      }

      if (this.lookingForLicense) {
        this.oneMoreTime = this.licenseNumber;
        return;
      }

      await this.resetValuesForLicenseLookup();
      this.lookingForLicense = true;

      // All the license magic goes here
      try {
        this.foundLicenseData = await this.$store.dispatch('properties/getLicenseInformation',
          { licenseNumber: this.licenseNumber, propertyType: this.propertyType, opaAccountNumber: null }
        );

        if (this.foundLicenseData === false) {
          this.errorLicenseNotFound = true;
        } else if (this.selectedProperty.opa_account_num != this.foundLicenseData.opa_account_num
          && this.foundLicenseData.opa_account_num != null) {
          this.errorOPADoesntMatch = true;
        } else {
          // We are clear to continue here.
          let realUnits = this.foundLicenseData.numberofunits;

          // If owner occupied and more than 1 units then, we need to subtract one unit.
          if (String(this.foundLicenseData.owneroccupied).toLowerCase() === 'yes' && realUnits > 1) {
            realUnits = realUnits - 1;
          }
          this.serverNumberOfUnits = realUnits;
          this.numberOfUnits = realUnits;
        }
      } catch (err) {
        // License is golden!
      }

      this.lookingForLicense = false;

      if (this.oneMoreTime) {
        this.oneMoreTime = null;
        this.checkLicense();
      }
    }, 400, false),

    maxNumericLength($event) {
      this.$nextTick(async () => {
        $event.target.value = String($event.target.value)
          .replace(/\D/g,'')
          .slice(0, $event.target.maxlength) || '';

        if ($event.target.name === 'licenseNumber') {
          await this.resetValuesForLicenseLookup();
          this.checkLicense();
        }
      });
    },
    async validateForm() {
      const valid = await this.$refs['property-form'].validate();
      if (!valid) {
        return false;
      }

      // Check some errors first
      if (this.errorOPADoesntMatch || this.errorLicenseNotFound) {
        this.$warning('Please fix all issues before continuing...');
        return;
      }

      this.$modal.show('confirm-data-modal');
      return;
    },

    async submitForm() {
      this.submitting = true;

      // Prepare data to submit
      let submitData = {};
      for (let key in this.$data) {
        if (!this[key] && typeof this[key] === 'string') {
          this[key] = null;
        }

        if (![ 'submitting' ].includes(key)) {
          /**
           * TODO: Find a better way to do this, this is super ugly!
           */
          if (this[key] === 'yes') {
            submitData[key] = true;
          } else if (this[key] === 'no') {
            submitData[key] = false;
          } else {
            submitData[key] = this[key];
          }
        }
      }

      if (!this.isAdmin) {
        // If it is not an admin, then some validations have to occur.
        if (this.dontHaveLicenseNumber) {
          submitData.licenseNumber = null;
        } else {
          // If the users enters a license, then, all this data has to be null.
          submitData.proprietorType = null;
          submitData.proprietorFirstName = null;
          submitData.proprietorMiddleName = null;
          submitData.proprietorLastName = null;
          submitData.proprietorCompanyName = null;
          submitData.proprietorJobTitle = null;
          submitData.proprietorEmailAddress = null;
          submitData.proprietorPhoneNumber = null;
          submitData.proprietorStreetAddress1 = null;
          submitData.proprietorStreetAddress2 = null;
          submitData.proprietorCity = null;
          submitData.proprietorState = null;
          submitData.proprietorCountry = null;
          submitData.proprietorZip = null;
        }
      }

      submitData.isCityRecord1978 = null;

      if (submitData.prior1978) {
        submitData.yearAssert = null;
      } else {
        if (this.mightBeBefore1978) {
          // This means that the city considers this property before 1978.
          submitData.isCityRecord1978 = false;
        } else {
          // This means city records indicates that the property was indeed built after 1978
          submitData.isCityRecord1978 = true;
          submitData.yearAssert = null;
        }
      }

      if (submitData.propertyType === 'Residential' && !submitData.numberOfUnits) {
        this.$warning('Enter a valid number of units please');
        return;
      }
      submitData.numberOfUnits = parseInt(this.numberOfUnits);

      if (submitData.propertyType === 'Daycare') {
        submitData.numberOfUnits = 1;
        submitData.childUnderSix = null;
      }
      submitData.opaAccountNumber = this.selectedProperty.opa_account_num;
      submitData.propertyZipCode = this.selectedProperty.zip_code;

      // If this is the admin form, we will remove some fields that are not needed
      if (this.isAdmin) {
        submitData.dontHaveLicenseNumber = undefined;
        submitData.formAssert = undefined;
        submitData.yearAssert = undefined;
      }

      // Verify if there are files to submit.
      if (!this.isAdmin) {
        submitData = objectToFormData(submitData);
        if (this.after1978Support.length) {
          this.after1978Support.forEach(file => {
            if (typeof file === 'object') {
              submitData.append("caseFiles", file);
            }
          });
        }
      }

      this.submitting = true;
      const uuid = await this.$store.dispatch('properties/submitElegibilityCheck', submitData);
      this.submitting = false;

      if (uuid === '') {
        // create a message and send the user to home. =/
        this.$warning(`We had an error submitting the form, verify in your "My Submissions" table if your data was created before trying again.`);
        this.$router.push({ name: 'home' });
        return;
      } else if (uuid !== false && typeof uuid === 'string') {
        this.$router.push({ name: 'submissions-details', params: { uuid }});
        return;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .custom-modal .has-text-weight-semibold {
    margin-bottom: 0;
  }
  .middle-name {
    max-width: 80px;
  }
  .table {
    tbody {
      tr {
        th {
          width: 250px !important;
        }

        th, td {
          vertical-align: middle;
        }
      }
    }
  }
</style>
